import React from 'react';
import { Empty } from 'antd';
import { usePermissions } from './usePermissions';

// CR004 — declarative permission gate. Renders children only when the active
// user holds the permission(s); otherwise renders `fallback` (nothing by default).
// Super-admin always passes (handled in usePermissions).
//
//   <Can permission={Permission.CREATE_SITE}><Button>New</Button></Can>
//   <Can any={[Permission.VIEW_USERS, Permission.EDIT_USER]}>…</Can>
//   <Can all={[Permission.EDIT_SITE, Permission.VIEW_SITES]}>…</Can>
export const Can: React.FC<
  React.PropsWithChildren<{
    permission?: string;
    any?: string[];
    all?: string[];
    fallback?: React.ReactNode;
  }>
> = ({ permission, any, all, fallback = null, children }) => {
  const { has, hasAny, hasAll } = usePermissions();

  let allowed = true;
  if (permission) allowed = has(permission);
  else if (all && all.length) allowed = hasAll(all);
  else if (any && any.length) allowed = hasAny(any);

  return <>{allowed ? children : fallback}</>;
};

// Route-level guard: renders the page only when the user has access, otherwise a
// friendly "no access" panel (no redirect, so there's no loop risk). Web users
// all have ADMIN_PORTAL, so this gates individual sections within the portal.
export const RequirePermission: React.FC<
  React.PropsWithChildren<{
    permission?: string;
    any?: string[];
    all?: string[];
  }>
> = ({ permission, any, all, children }) => {
  const { has, hasAny, hasAll } = usePermissions();

  let allowed = true;
  if (permission) allowed = has(permission);
  else if (all && all.length) allowed = hasAll(all);
  else if (any && any.length) allowed = hasAny(any);

  if (allowed) return <>{children}</>;
  return (
    <div className="w-full flex items-center justify-center p-16">
      <Empty description="You don’t have access to this section. Ask an admin if you need it." />
    </div>
  );
};

export default Can;
